import React from 'react';
import { Search } from 'lucide-react';

export type FilterState = 'all' | 'unreviewed' | 'edited' | 'excluded';

interface ValidationFilterBarProps {
  activeFilter: FilterState;
  searchValue: string;
  onFilterChange: (filter: FilterState) => void;
  onSearchChange: (value: string) => void;
  counts: {
    all: number;
    unreviewed: number;
    edited: number;
    excluded: number;
  };
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

const FILTER_CHIPS: { key: FilterState; label: string; testId: string }[] = [
  { key: 'all', label: 'All', testId: 'filter-state-all' },
  { key: 'unreviewed', label: 'Unreviewed', testId: 'filter-state-unreviewed' },
  { key: 'edited', label: 'Edited', testId: 'filter-state-edited' },
  { key: 'excluded', label: 'Excluded', testId: 'filter-state-excluded' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const SELECT_CLS =
  'h-[34px] px-2 text-sm rounded-lg border border-border' +
  'bg-bg-surface text-text-primary' +
  'focus:outline-none focus:ring-2 focus:ring-interactive-primary';

export const ValidationFilterBar: React.FC<ValidationFilterBarProps> = ({
  activeFilter,
  searchValue,
  onFilterChange,
  onSearchChange,
  counts,
  pageSize,
  onPageSizeChange,
}) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {FILTER_CHIPS.map(({ key, label, testId }) => (
        <button
          key={key}
          data-testid={testId}
          onClick={() => onFilterChange(key)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeFilter === key
              ? 'bg-interactive-primary text-text-inverse'
              : 'bg-bg-subtle text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {label}
          <span className="ml-1.5 text-xs opacity-75">
            ({(counts[key] ?? 0).toLocaleString()})
          </span>
        </button>
      ))}

      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search part number or name..."
          data-testid="validation-search"
          className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-border bg-bg-surface text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-interactive-primary"
        />
      </div>

      <select
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        data-testid="validation-page-size-select"
        className={SELECT_CLS}
        aria-label="Rows per page"
      >
        {PAGE_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>{n} per page</option>
        ))}
      </select>
    </div>
  );
};
